
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import settingx from '@/store/modules/settingx';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import SecondaryLineBarChartFilterable from '@/components/reports-v3/components/filterables/SecondaryLineBarChartFilterable.vue';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import {TableItemFormatter} from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import echarts from 'echarts';
import gdbx from '@/store/modules/gdbx';
import {DataViewFormatter} from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import moment from 'moment/moment';
import {COL, getTableName, sqlExtractYear, sqlExtractMonth, sqlSumByDocType} from '@/worker/db/sqlHelpers';

@Component({
  components: {
    TableFilterable,
    IncludeCreditNoteToggle,
    IncludeDebitNoteToggle,
    SecondaryLineBarChartFilterable,
  },
})

export default class PalmKingDashboard extends FilterWidget {

  public filterIds: Array<
      | 'date'
      | 'dateAsOf'
      | 'dateRange'
      | 'stockItems'
      | 'agents'
      | 'customers'
      | 'suppliers'
      | 'defaultAgent'
  > = ['stockItems', 'dateRange', 'defaultAgent'];

  public tableItems: any[] = [];
  public tableFields: any[] = [];

  public xAxisData: any [] = [];
  public series: echarts.ECharts[] | any[] = [];
  public seriesData: any = {
    localData: null,
    overseasData: null,
    totalData: null,
  };

  public companyTableItems: any[] = [];
  public companyTableFields: any[] = [];

  public companyTableItems2: any[] = [];
  public companyTableFields2: any[] = [];

  public get exportFileName() {
    return 'Monthly Sales Table as of ' + this.monthName + ' ' + this.yearName;
  }

  public get exportFileName2() {
    return 'Monthly Sales Chart as of ' + this.monthName + ' ' + this.yearName;
  }

  public get exportFileName3() {
    return 'Monthly Sales by Company as of ' + this.monthName + ' ' + this.yearName;
  }

  public get exportFileName4() {
    return 'Monthly Sales Local Overseas as of ' + this.monthName + ' ' + this.yearName;
  }

  public get currencySymbol() {
    return gdbx.currencySymbol;
  }

  public get includeCreditNote() {
    return settingx.now.includeCreditNote;
  }

  public get includeDebitNote() {
    return settingx.now.includeDebitNote;
  }

  public get currentMonth() {
    return moment(this.selectedAsOfDate[1]).month();
  }

  public get currentYear() {
    return moment(this.selectedAsOfDate[1]).year();
  }

  public get yearToDate(): [number, number] {
      return [
          moment(this.selectedDateRange[1]).startOf('year').valueOf(),
          moment(this.selectedDateRange[1]).valueOf(),
      ];
  }

  public get monthName() {
    return moment().month(this.currentMonth).format('MMM').toUpperCase();
  }

  public get yearName() {
    return moment().year(this.currentYear).format('YY').toUpperCase();
  }

  public get dataViewFormatter() {
    return DataViewFormatter.salesTracker;
  }

  public get expensiveHook() {
    const {
      selectedDateRange,
      selectedAsOfDate,
      selectedAgents,
      selectedStockItems,
      includeCreditNote,
      includeDebitNote,
    } = this;
    return JSON.stringify([
      selectedDateRange,
      selectedAsOfDate,
      selectedAgents,
      selectedStockItems,
      includeCreditNote,
      includeDebitNote,
    ]);
  }
  public async expensiveCalc() {
    const jobId = this.currentJobId;
    this.generateLoadingText(0.2, 0.3);

    const queryHead = await this._ensureTablesLoaded(['diySalesSummary'] as any[]);
    this.checkInterrupted(jobId);
    this.generateLoadingText(0.3, 0.9);

    const tableName = getTableName('diySalesSummary');
    const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;

    // Helper for conditional sums with docType + type filter
    const sumByDocAndType = (
        docTypes: string[], type: string, col: string, alias: string,
    ) => {
      const typeList = docTypes.map((t) => `'${t}'`).join(', ');
      return `SUM(CASE WHEN ${COL.DOCTYPE} IN (${typeList}) AND "TYPE" = '${type}' `
          + `THEN TRY_CAST(${col} AS DOUBLE) ELSE 0 END) as ${alias}`;
    };

    // Query 1: Main aggregation by year/month (all doc types + local/overseas)
    const mainQuery = queryHead.rawQuery(`
        SELECT
            ${sqlExtractYear(COL.DOCDATE)},
            ${sqlExtractMonth(COL.DOCDATE)},
            ${sqlSumByDocType(['IV', 'CS'], COL.AMOUNT, 'iv_amount')},
            ${sqlSumByDocType(['DN'], COL.AMOUNT, 'dn_amount')},
            ${sqlSumByDocType(['CN'], COL.AMOUNT, 'cn_amount')},
            ${sqlSumByDocType(['IV', 'CS'], '"REFCOST"', 'iv_refcost')},
            ${sumByDocAndType(['IV', 'CS'], 'LOCAL', '"AMOUNT"', 'local_iv_amount')},
            ${sumByDocAndType(['DN'], 'LOCAL', '"AMOUNT"', 'local_dn_amount')},
            ${sumByDocAndType(['CN'], 'LOCAL', '"AMOUNT"', 'local_cn_amount')},
            ${sumByDocAndType(['IV', 'CS'], 'OVERSEA', '"AMOUNT"', 'oversea_iv_amount')},
            ${sumByDocAndType(['DN'], 'OVERSEA', '"AMOUNT"', 'oversea_dn_amount')},
            ${sumByDocAndType(['CN'], 'OVERSEA', '"AMOUNT"', 'oversea_cn_amount')}
        FROM ${tableName}
        WHERE ${dateFilter}
        GROUP BY year, month
        ORDER BY year, month
    `);

    // Query 2: Company category aggregation by year/month/category
    const catQuery = queryHead.rawQuery(`
        SELECT
            "COMPANYCATEGORY" as category,
            ${sqlExtractYear(COL.DOCDATE)},
            ${sqlExtractMonth(COL.DOCDATE)},
            ${sqlSumByDocType(['IV', 'CS'], COL.AMOUNT, 'iv_amount')},
            ${sqlSumByDocType(['DN'], COL.AMOUNT, 'dn_amount')},
            ${sqlSumByDocType(['CN'], COL.AMOUNT, 'cn_amount')}
        FROM ${tableName}
        WHERE ${dateFilter}
        GROUP BY category, year, month
        ORDER BY category, year, month
    `);

    const [mainRows, catRows] = await Promise.all([mainQuery, catQuery]);
    this.checkInterrupted(jobId);
    this.generateLoadingText(0.8, 0.95);

    // Generate month labels from date range
    const monthLabels: string[] = [];
    const monthKeys: string[] = [];
    const mmt = moment(this.selectedDateRange[0]);
    const endMmt = moment(this.selectedDateRange[1]);

    while (mmt.isSameOrBefore(endMmt, 'month')) {
      monthLabels.push(mmt.format('MMM YY'));
      monthKeys.push(`${mmt.year()}-${mmt.month() + 1}`);
      mmt.add(1, 'month');
    }

    // Build main data map: monthKey -> row
    const mainMap = new Map<string, any>();
    for (const row of mainRows) {
      mainMap.set(`${row.year}-${row.month}`, row);
    }

    const getVal = (key: string, field: string) => {
      const r = mainMap.get(key);
      return r ? (Number(r[field]) || 0) : 0;
    };

    // === Monthly Sales Table ===
    this.tableItems = monthKeys.map((key, i) => {
      let sales = getVal(key, 'iv_amount');
      if (this.includeDebitNote) { sales += getVal(key, 'dn_amount'); }
      if (this.includeCreditNote) { sales -= getVal(key, 'cn_amount'); }

      const cost = getVal(key, 'iv_refcost');
      const margin = sales - cost;

      return {
        month: monthLabels[i],
        sales,
        cost,
        margin,
        margin_percentage: (margin / sales) * 100,
      };
    });

    this.tableFields = [
      { key: 'month', sortable: false },
      { key: 'sales', sortable: false, formatter: TableItemFormatter.currencySplit },
      { key: 'cost', sortable: false, formatter: TableItemFormatter.currencySplit },
      { key: 'margin', sortable: false, formatter: TableItemFormatter.currencySplit },
      { key: 'margin_percentage', sortable: false, formatter: TableItemFormatter.percentage },
    ];

    // === Bar Chart Data (DN/CN toggles apply to BOTH local and overseas) ===
    const localSales = monthKeys.map((key) => {
      let val = getVal(key, 'local_iv_amount');
      if (this.includeDebitNote) { val += getVal(key, 'local_dn_amount'); }
      if (this.includeCreditNote) { val -= getVal(key, 'local_cn_amount'); }
      return val;
    });

    const overseasSales = monthKeys.map((key) => {
      let val = getVal(key, 'oversea_iv_amount');
      if (this.includeDebitNote) { val += getVal(key, 'oversea_dn_amount'); }
      if (this.includeCreditNote) { val -= getVal(key, 'oversea_cn_amount'); }
      return val;
    });

    const totalSales = localSales.map((local, i) => local + overseasSales[i]);

    this.seriesData.localData = localSales;
    this.seriesData.overseasData = overseasSales;
    this.seriesData.totalData = totalSales;

    this.xAxisData = monthLabels;

    this.series = [
      { name: 'Local', data: localSales, type: 'bar', barWidth: '25%' },
      { name: 'Overseas', data: overseasSales, type: 'bar', barWidth: '25%' },
      { name: 'Total', data: totalSales, type: 'bar', barWidth: '25%' },
    ];

    // === Company Table (DN/CN toggles apply to total_local ONLY, not overseas) ===
    this.companyTableItems = monthKeys.map((key, i) => {
      let totalLocal = getVal(key, 'local_iv_amount');
      if (this.includeDebitNote) { totalLocal += getVal(key, 'local_dn_amount'); }
      if (this.includeCreditNote) { totalLocal -= getVal(key, 'local_cn_amount'); }

      const totalOversea = getVal(key, 'oversea_iv_amount');

      return {
        month: monthLabels[i],
        total_local: totalLocal,
        total_overseas: totalOversea,
        total: totalLocal + totalOversea,
      };
    });

    this.companyTableFields = [
      { key: 'month', sortable: false },
      { key: 'total_local', sortable: false, formatter: TableItemFormatter.currencySplit },
      { key: 'total_overseas', sortable: false, formatter: TableItemFormatter.currencySplit },
      { key: 'total', sortable: false, formatter: TableItemFormatter.currencySplit },
    ];

    // === Company Category Table ===
    const categories: string[] = [];
    const catMap = new Map<string, Map<string, any>>();

    for (const row of catRows) {
      const cat = String(row.category || '');
      if (!catMap.has(cat)) {
        catMap.set(cat, new Map());
        categories.push(cat);
      }
      catMap.get(cat)!.set(`${row.year}-${row.month}`, row);
    }

    // Set fields BEFORE items as a complete array to avoid
    // PrimeVue DataTable rendering stale columns on first paint
    this.companyTableFields2 = [
      { key: 'month', sortable: false },
      ...categories.map((cat) => ({
        key: cat,
        sortable: false,
        formatter: TableItemFormatter.currencySplit,
      })),
    ];

    this.companyTableItems2 = monthKeys.map((key, i) => {
      const item: any = { month: monthLabels[i] };

      for (const cat of categories) {
        const catMonthMap = catMap.get(cat)!;
        const catRow = catMonthMap.get(key);

        let val = catRow ? (Number(catRow.iv_amount) || 0) : 0;
        if (this.includeDebitNote) {
          val += catRow ? (Number(catRow.dn_amount) || 0) : 0;
        }
        if (this.includeCreditNote) {
          val -= catRow ? (Number(catRow.cn_amount) || 0) : 0;
        }

        item[cat] = val;
      }

      return item;
    });

    this.generateLoadingText(1);

    this.saveHistory(
        'tableItems',
        'tableFields',
        'series',
        'seriesData',
        'xAxisData',
        'companyTableItems',
        'companyTableFields',
        'companyTableItems2',
        'companyTableFields2',
    );
  }
}
